import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../apiConfig';
import styles from './FeedbackWidget.module.css';

const FeedbackWidget = ({ onFeedbackSent, className = '' }) => {
  const { currentUser, loading } = useAuth();
  const user = currentUser;
  const isAuthenticated = !!currentUser;
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [context, setContext] = useState(null);
  
  const drawerRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const buttonRef = useRef(null);

  // Email validation function
  const validateEmail = (email) => {
    if (!email) return ''; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? '' : 'Please enter a valid email address';
  };
  
  // Auto-fill name and email from authenticated user or Shopify customer
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
      setName(user.displayName || '');
    } else if (typeof window !== 'undefined' && window.CUSTOMER_EMAIL) {
      setEmail(window.CUSTOMER_EMAIL);
    }
  }, [user]);

  // Detect page context (artwork, article, etc.)
  useEffect(() => {
    const detectContext = () => {
      const pathname = window.location.pathname;
      
      // Check for artwork pages
      const artworkMatch = pathname.match(/\/artworks?\/([a-f0-9-]{36})/i);
      if (artworkMatch) {
        setContext({
          type: 'artwork',
          id: artworkMatch[1],
          label: 'This artwork'
        });
        return;
      }

      // Check for article pages
      const articleMatch = pathname.match(/\/articles?\/([a-f0-9-]{36})/i);
      if (articleMatch) {
        setContext({
          type: 'article',
          id: articleMatch[1],
          label: 'This article'
        });
        return;
      }

      // Check for exhibition pages
      const exhibitionMatch = pathname.match(/\/exhibitions?\/([a-f0-9-]{36})/i);
      if (exhibitionMatch) {
        setContext({
          type: 'exhibition',
          id: exhibitionMatch[1],
          label: 'This exhibition'
        });
        return;
      }

      setContext(null);
    };

    detectContext();
  }, []);

  // Load draft from localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const draftKey = `feedback-draft-${user?.uid || 'anonymous'}-${window.location.pathname}`;
      const draft = localStorage.getItem(draftKey);
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          setCategory(parsed.category || '');
          setMessage(parsed.message || '');
          setName(parsed.name || name);
          if (!user?.email) {
            setEmail(parsed.email || email);
          }
        } catch (e) {
          // Ignore invalid draft
        }
      }
    }
  }, [email, user]);

  // Save draft to localStorage
  const saveDraft = useCallback(() => {
    if (typeof window !== 'undefined' && (category || message)) {
      const draftKey = `feedback-draft-${user?.uid || 'anonymous'}-${window.location.pathname}`;
      localStorage.setItem(draftKey, JSON.stringify({ category, message, email, name }));
    }
  }, [category, message, email, name, user]);

  useEffect(() => {
    saveDraft();
  }, [saveDraft]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      firstFocusableRef.current?.focus();
    }
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setErrors({});
    buttonRef.current?.focus();
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!category) {
      newErrors.category = 'Please select a feedback type';
    }
    
    if (!message.trim() && !screenshot) {
      newErrors.message = 'Please provide either a message or screenshot';
    }
    
    // Only validate email on form submission, and only if user entered something
    if (email.trim()) {
      const emailError = validateEmail(email);
      if (emailError) {
        newErrors.email = emailError;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('category', category);
      formData.append('message', message);
      formData.append('name', name);
      formData.append('email', email);
      formData.append('url', window.location.href);
      formData.append('userAgent', navigator.userAgent);
      
      if (screenshot) {
        formData.append('screenshot', screenshot);
      }
      
      // Add authentication header if user is logged in
          const headers = {};
    if (isAuthenticated && user?.getIdToken) {
      try {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.warn('Failed to get auth token:', error);
      }
    }

      const response = await api.post('/feedback', formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.status !== 200) {
        throw new Error('Failed to submit feedback');
      }
      
      // Clear draft
      if (typeof window !== 'undefined') {
        const draftKey = `feedback-draft-${user?.uid || 'anonymous'}-${window.location.pathname}`;
        localStorage.removeItem(draftKey);
      }
      
      // Reset form
      setCategory('');
      setMessage('');
      setName(user?.displayName || '');
      setEmail(user?.email || window.CUSTOMER_EMAIL || '');
      setScreenshot(null);
      setErrors({});
      
      // Show success
      setShowSuccess(true);
      
      // Call callback
      if (onFeedbackSent) {
        onFeedbackSent({ category, message, name, email });
      }
      
      // Auto-close after success
      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
      }, 1500);
      
    } catch (error) {
      setErrors({ submit: 'Failed to submit feedback. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setScreenshot(file);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(true)}
        className={`${styles.feedbackButton} ${className}`}
        aria-label="Open feedback form"
      >
        <div className={styles.feedbackButtonContent}>
          <svg
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <span className={styles.feedbackButtonText}>Feedback</span>
        </div>
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className={styles.backdrop}
          onClick={handleClose}
        />
      )}

      {/* Drawer */}
      {isOpen && (
        <div
          ref={drawerRef}
          className={styles.drawer}
        >
          {/* Header */}
          <div className={styles.drawerHeader}>
            <h2 className={styles.drawerTitle}>
              Send Feedback
            </h2>
            <button
              onClick={handleClose}
              className={styles.closeButton}
              aria-label="Close feedback form"
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Success Message */}
          {showSuccess && (
            <div className={styles.successMessage}>
              <div className={styles.successContent}>
                <svg className={styles.successIcon} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className={styles.successText}>
                  Feedback sent successfully!
                </span>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Context Indicator */}
            {context && (
              <div className={styles.contextIndicator}>
                <div className={styles.contextContent}>
                  <svg className={styles.contextIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={styles.contextText}>
                    Feedback about: <span className={styles.contextLabel}>{context.label}</span>
                  </span>
                </div>
              </div>
            )}

            {/* Category Selection */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                What type of feedback?
              </label>
              <div className={styles.categoryOptions}>
                {[
                  { value: 'comment', label: 'Leave a comment', desc: 'General thoughts or suggestions' },
                  { value: 'issue', label: 'Report an issue', desc: 'Something isn\'t working correctly' },
                  { value: 'message', label: 'Message us', desc: 'Contact our team directly' }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={styles.categoryOption}
                  >
                    <input
                      ref={option.value === 'comment' ? firstFocusableRef : null}
                      type="radio"
                      name="category"
                      value={option.value}
                      checked={category === option.value}
                      onChange={(e) => setCategory(e.target.value)}
                      className={styles.categoryRadio}
                    />
                    <div className={styles.categoryContent}>
                      <div className={styles.categoryTitle}>{option.label}</div>
                      <div className={styles.categoryDescription}>{option.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.category && (
                <p className={styles.errorMessage}>{errors.category}</p>
              )}
            </div>

            {/* Message */}
            <div className={styles.formGroup}>
              <label htmlFor="message" className={styles.label}>
                Message {!screenshot && <span style={{color: '#dc2626'}}>*</span>}
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className={styles.textarea}
                placeholder="Tell us what's on your mind..."
              />
              {errors.message && (
                <p className={styles.errorMessage}>{errors.message}</p>
              )}
            </div>

            {/* Name */}
            <div className={styles.formGroup}>
              <label htmlFor="name" className={styles.label}>
                Name (optional)
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={styles.input}
                placeholder="Your name"
              />
            </div>

            {/* Email */}
            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.label}>
                Email (optional)
              </label>
              <input
                type="text"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // Clear any existing email errors when user types
                  if (errors.email) {
                    setErrors(prev => ({ ...prev, email: '' }));
                  }
                }}
                className={styles.input}
                placeholder="your@email.com"
                spellCheck="false"
                autoCorrect="off"
                autoCapitalize="off"
              />
              {errors.email && (
                <p className={styles.errorMessage}>{errors.email}</p>
              )}
            </div>

            {/* Screenshot */}
            <div className={styles.formGroup}>
              <label htmlFor="screenshot" className={styles.label}>
                Screenshot (optional)
              </label>
              <div className={styles.fileInput}>
                <input
                  type="file"
                  id="screenshot"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </div>
              {screenshot && (
                <p className={styles.fileSelected}>
                  File selected: {screenshot.name}
                </p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <p className={styles.errorMessage}>{errors.submit}</p>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.submitButton}
            >
              {isSubmitting ? (
                <div className={styles.loadingContent}>
                  <svg className={styles.spinner} fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{opacity: 0.25}} />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" style={{opacity: 0.75}} />
                  </svg>
                  Sending...
                </div>
              ) : (
                'Send Feedback'
              )}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget; 